import type React from 'react';

import { Box, Button, ButtonGroup, TextButton } from '@zillow/constellation-10';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';

import { GET_STARTED, NEXT_TO_SECTION_TEXT, VIEW_SUMMARY } from './SectionWrapper/constants';
import { LEASE_TYPES_ANALYTICS } from '../constants/analytics';
import { useLeasingUrlParams } from '../hooks/useLeasingUrlParams';
import type { Lease } from '../models';
import { useWebAnalyticsForListingQuery } from '../utils/queries';
import {
  emitGetStartedLeaseEvent,
  emitLandlordInformationUploadLeaseSaveAndContinueEvent,
  emitLeaseTermsUploadLeaseSaveAndContinueEvent,
  emitRentAmountUploadLeaseSaveAndContinueEvent,
  emitSaveAndContinueEvent,
  emitSectionEightSaveAndContinueEvent,
  emitSecurityDepositUploadLeaseSaveAndContinueEvent,
  emitTenantInformationUploadLeaseSaveAndContinueEvent,
  emitViewSummaryEvent,
} from '../../analytics/events';

type OwnProps = {
  showBack: boolean;
  getPrevious(): void;
  hasNext: boolean;
  hasPrevious: boolean;
  isDisabled: boolean;
  submitHandler(): void;
  hasFieldChanged: boolean;
  submitText: string;
  renderModalView: boolean;
  leaseType?: string;
  uiSection?: string;
  lease: Lease;
  isLoading?: boolean;
};

BackAndSubmit.defaultProps = {
  hasNext: false,
  hasPrevious: false,
  isDisabled: false,
};

export function BackAndSubmit({
  showBack,
  getPrevious,
  isDisabled,
  submitHandler,
  submitText,
  renderModalView,
  leaseType,
  uiSection,
  isLoading = false,
}: OwnProps): JSX.Element {
  const { user } = useRentalManagerUser();
  const { propertyId } = useLeasingUrlParams();
  const webAnalyticsQuery = useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });
  const zrm_listing_info = webAnalyticsQuery.data;

  function onPreviousClick(): void {
    getPrevious();
  }

  function onSubmitClick(): void {
    if (submitText === VIEW_SUMMARY) {
      emitViewSummaryEvent({ user, listing: zrm_listing_info });
    } else if (submitText === GET_STARTED) {
      emitGetStartedLeaseEvent({ user, listing: zrm_listing_info });
    }
    if (uiSection?.includes('Section8HousingVoucher')) {
      emitSectionEightSaveAndContinueEvent({ user, listing: zrm_listing_info });
    } else if (
      uiSection?.includes('LeaseTerms') &&
      (leaseType === LEASE_TYPES_ANALYTICS.SELF_UPLOAD || leaseType === LEASE_TYPES_ANALYTICS.SIGNED_SELF_UPLOAD)
    ) {
      emitLeaseTermsUploadLeaseSaveAndContinueEvent({ user, listing: zrm_listing_info });
    } else if (
      uiSection?.includes('TenantInformation') &&
      (leaseType === LEASE_TYPES_ANALYTICS.SELF_UPLOAD || leaseType === LEASE_TYPES_ANALYTICS.SIGNED_SELF_UPLOAD)
    ) {
      emitTenantInformationUploadLeaseSaveAndContinueEvent({ user, listing: zrm_listing_info });
    } else if (
      uiSection?.includes('LandlordInformation') &&
      (leaseType === LEASE_TYPES_ANALYTICS.SELF_UPLOAD || leaseType === LEASE_TYPES_ANALYTICS.SIGNED_SELF_UPLOAD)
    ) {
      emitLandlordInformationUploadLeaseSaveAndContinueEvent({ user, listing: zrm_listing_info });
    } else if (uiSection === 'RentalProtectionBaseRent') {
      emitRentAmountUploadLeaseSaveAndContinueEvent({ user, listing: zrm_listing_info });
    } else if (uiSection === 'RentalProtectionSecurityDeposit') {
      emitSecurityDepositUploadLeaseSaveAndContinueEvent({ user, listing: zrm_listing_info });
    }
    if (submitText === NEXT_TO_SECTION_TEXT && leaseType === LEASE_TYPES_ANALYTICS.ZILLOW_LEASE) {
      emitSaveAndContinueEvent({ user, listing: zrm_listing_info });
    }
    submitHandler();
  }

  function renderBack(): React.ReactNode {
    return (
      showBack && (
        <TextButton tone="brand" onClick={onPreviousClick} disabled={isDisabled} data-test="back-button">
          Back
        </TextButton>
      )
    );
  }

  function renderSubmit(): React.ReactNode {
    return (
      <Button
        tone="brand"
        emphasis="filled"
        data-test="submit"
        disabled={isDisabled}
        loading={isLoading}
        onClick={onSubmitClick}
      >
        {submitText}
      </Button>
    );
  }

  return renderModalView ? (
    <ButtonGroup aria-label="back and submit">
      {renderBack()}
      {renderSubmit()}
    </ButtonGroup>
  ) : (
    <Box
      css={{
        marginTop: 'loose',
        display: 'flex',
        justifyContent: showBack ? 'space-between' : 'flex-end',
        alignItems: 'center',
      }}
    >
      {renderBack()}
      {renderSubmit()}
    </Box>
  );
}

export default BackAndSubmit;
